import { useQuery } from 'react-query';
import APIHelper from '../../services/api.helper';
import { getCategories } from './helpers';

const useFiltersQuery = (endpoint, ...props) =>
  useQuery(
    ['timeSeriesFiltersQuery', endpoint],
    () =>
      fetch(endpoint, {
        credentials: 'include',
      }).then(APIHelper.parseJSON),
    ...props,
  );

const useFilters = (
  { stattype, system, leadtime, area, source, stat, level, dates },
  view,
) => {
  const categories = getCategories(view);

  const defaultQuery = useFiltersQuery(
    `${global.env.API_HOST}${
      view === 'timeserie' ? 'timeseries' : view
    }/default`,
    {
      enabled: stattype === undefined,
    },
  );

  const statTypes = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type?filter_type=${view}`,
    {
      enabled: stattype !== undefined,
    },
  );

  const selectedStatType =
    statTypes.data?.values.find(({ id }) => id === Number(stattype)) ??
    statTypes.data?.default;

  const systems = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type/${selectedStatType?.id}/system?filter_type=${view}`,
    {
      enabled: Boolean(stattype && statTypes.data),
    },
  );

  const selectedSystem =
    systems.data?.values.find(({ id }) => id === Number(system)) ??
    systems.data?.default;

  const leadtimes = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type/${selectedStatType?.id}/system/${selectedSystem?.id}/leadtime?filter_type=${view}`,
    {
      enabled: Boolean(stattype && statTypes.data && systems.data),
    },
  );

  const selectedLeadtime =
    leadtimes.data?.values.find(({ id }) => id === Number(leadtime)) ??
    leadtimes.data?.default;

  const areas = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type/${selectedStatType?.id}/system/${selectedSystem?.id}/leadtime/${selectedLeadtime?.id}/area?filter_type=${view}`,
    {
      enabled: Boolean(
        stattype && statTypes.data && systems.data && leadtimes.data,
      ),
    },
  );

  const selectedArea =
    areas.data?.values.find(({ id }) => id === Number(area)) ??
    areas.data?.default;

  const sources = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type/${selectedStatType?.id}/system/${selectedSystem?.id}/leadtime/${selectedLeadtime?.id}/area/${selectedArea?.id}/source?filter_type=${view}`,
    {
      enabled: Boolean(
        stattype &&
        statTypes.data &&
        systems.data &&
        leadtimes.data &&
        areas.data,
      ),
    },
  );

  const selectedSource =
    sources.data?.values.find(({ id }) => id === Number(source)) ??
    sources.data?.default;

  const stats = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type/${selectedStatType?.id}/system/${selectedSystem?.id}/leadtime/${selectedLeadtime?.id}/area/${selectedArea?.id}/source/${selectedSource?.id}/stat?filter_type=${view}`,
    {
      enabled: Boolean(
        stattype &&
        statTypes.data &&
        systems.data &&
        leadtimes.data &&
        areas.data &&
        sources.data,
      ),
    },
  );

  const selectedStat =
    stats.data?.values.find(({ id }) => id === Number(stat)) ??
    stats.data?.default;

  const levels = useFiltersQuery(
    `${global.env.API_HOST}timeseries/default/stat_type/${selectedStatType?.id}/system/${selectedSystem?.id}/leadtime/${selectedLeadtime?.id}/area/${selectedArea?.id}/source/${selectedSource?.id}/stat/${selectedStat?.id}/level?filter_type=${view}`,
    {
      enabled: Boolean(
        categories.find(({ key }) => key === 'level') &&
        stattype &&
        statTypes.data &&
        systems.data &&
        leadtimes.data &&
        areas.data &&
        sources.data &&
        stats.data,
      ),
    },
  );

  const selectedLevel =
    levels.data?.values.find(({ id }) => id === Number(level)) ??
    levels.data?.default;

  const datesQuery = useFiltersQuery(
    `${global.env.API_HOST}vertical-profile/default/stat_type/${selectedStatType?.id}/system/${selectedSystem?.id}/leadtime/${selectedLeadtime?.id}/area/${selectedArea?.id}/source/${selectedSource?.id}/stat/${selectedStat?.id}/dates`,
    {
      enabled: Boolean(
        categories.find(({ key }) => key === 'dates') &&
        stattype &&
        statTypes.data &&
        systems.data &&
        leadtimes.data &&
        areas.data &&
        sources.data &&
        stats.data,
      ),
    },
  );

  const selectedDates =
    datesQuery.data?.values.find(item => item === dates) ??
    datesQuery.data?.default;

  const isLoading = [
    defaultQuery,
    statTypes,
    systems,
    leadtimes,
    areas,
    sources,
    stats,
    levels,
    datesQuery,
  ].some(item => item.isLoading);

  const output = [
    {
      isLoading,
      data:
        stattype === undefined
          ? defaultQuery.data
          : {
              stattype: {
                values: statTypes.data?.values,
                default: selectedStatType,
              },
              system: {
                values: systems.data?.values,
                default: selectedSystem,
              },
              leadtime: {
                values: leadtimes.data?.values,
                default: selectedLeadtime,
              },
              area: {
                values: areas.data?.values,
                default: selectedArea,
              },
              source: {
                values: sources.data?.values,
                default: selectedSource,
              },
              stat: {
                values: stats.data?.values,
                default: selectedStat,
              },
              level: {
                values: levels.data?.values,
                default: selectedLevel,
              },
              dates: {
                values: datesQuery.data?.values.map(value => ({
                  id: value,
                  name: value,
                })),
                default: { id: selectedDates, name: selectedDates },
              },
            },
    },
  ];

  return output;
};

export default useFilters;
